import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';

const DPAPage = () => {
  return (
    <div className="min-h-screen bg-white py-20">
      <SEOHead page="dpa" />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Data Processing Agreement</h1>
            <p className="text-lg text-slate-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="prose prose-slate max-w-none space-y-8">
            <div>
              <p className="text-slate-600">
                This Data Processing Agreement ("DPA") forms part of the agreement between Digian.ai ("Processor") and the customer entity that has accepted the Digian.ai Terms of Service ("Controller"). This DPA applies where Digian.ai processes Personal Data on behalf of the Controller in the course of providing its services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Definitions</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>"Personal Data"</strong> — any information relating to an identified or identifiable natural person, as defined under applicable Data Protection Laws.</li>
                <li><strong>"Processing"</strong> — any operation performed on Personal Data, including collection, storage, use, disclosure, or deletion.</li>
                <li><strong>"Data Protection Laws"</strong> — all applicable privacy and data protection legislation, including GDPR (EU) 2016/679, UK GDPR, and the California Consumer Privacy Act (CCPA), as applicable.</li>
                <li><strong>"Sub-processor"</strong> — any third party engaged by Digian.ai to process Personal Data on behalf of the Controller.</li>
                <li><strong>"Data Subject"</strong> — an individual whose Personal Data is processed under this DPA.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Scope and Purpose</h2>
              <p className="text-slate-600 mb-4">
                Digian.ai processes Personal Data solely as instructed by the Controller, for the purpose of delivering the contracted services (AI-powered analytics, voice agents, and IT support automation). The subject matter, nature, purpose, and duration of processing are as set out in the applicable service agreement and this DPA.
              </p>
              <p className="text-slate-600">
                Digian.ai shall not process Personal Data for its own commercial purposes, nor sell or rent it to third parties.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Controller Obligations</h2>
              <p className="text-slate-600 mb-2">The Controller represents and warrants that it:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Has a lawful basis for transferring Personal Data to Digian.ai for processing.</li>
                <li>Has provided all required notices to and obtained all required consents from Data Subjects.</li>
                <li>Will comply with applicable Data Protection Laws in its use of the services.</li>
                <li>Will ensure the accuracy and relevance of the Personal Data provided for processing.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Processor Obligations</h2>
              <p className="text-slate-600 mb-2">Digian.ai shall:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Process Personal Data only on documented instructions from the Controller, unless required by applicable law.</li>
                <li>Ensure that personnel authorised to process Personal Data are bound by appropriate confidentiality obligations.</li>
                <li>Implement and maintain appropriate technical and organisational security measures (see Section 6).</li>
                <li>Assist the Controller in responding to Data Subject rights requests within legally required timeframes.</li>
                <li>Assist the Controller in meeting its obligations relating to security, breach notification, data protection impact assessments, and prior consultation.</li>
                <li>Delete or return all Personal Data to the Controller upon termination of the services, at the Controller's choice, unless retention is required by law.</li>
                <li>Make available to the Controller all information necessary to demonstrate compliance with this DPA.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Sub-processors</h2>
              <p className="text-slate-600 mb-4">
                The Controller grants Digian.ai general authorisation to engage sub-processors, subject to the following conditions:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Digian.ai will maintain an up-to-date list of sub-processors and notify the Controller of any intended additions or replacements.</li>
                <li>The Controller may object to new sub-processors within 14 days of notification.</li>
                <li>Sub-processors are bound by data protection obligations equivalent to those in this DPA.</li>
                <li>Digian.ai remains fully liable for the acts and omissions of its sub-processors.</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Current sub-processors include cloud infrastructure (Microsoft Azure), email delivery services, and analytics platforms. A full list is available upon request at <a href="mailto:contact@digian.ai" className="text-indigo-600 hover:text-indigo-800">contact@digian.ai</a>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Security Measures</h2>
              <p className="text-slate-600 mb-4">
                Digian.ai implements appropriate technical and organisational measures to protect Personal Data against unauthorised or unlawful processing and accidental loss, destruction, or damage, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256)</li>
                <li>Access controls based on least-privilege principles and role-based permissions</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Logging and monitoring of system access and anomalous activity</li>
                <li>Incident response procedures and breach notification processes</li>
                <li>Employee security training and background checks for staff with data access</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. International Data Transfers</h2>
              <p className="text-slate-600">
                Where Personal Data is transferred outside the European Economic Area (EEA) or UK, Digian.ai will ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) approved by the European Commission, or other legally recognised transfer mechanisms. Transfers to sub-processors are subject to equivalent protections.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Data Breach Notification</h2>
              <p className="text-slate-600">
                Digian.ai will notify the Controller without undue delay, and in any case within 72 hours, upon becoming aware of a Personal Data breach. Notification will include, to the extent known: the nature of the breach, categories and approximate number of Data Subjects affected, likely consequences, and measures taken or proposed to address the breach.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Data Subject Rights</h2>
              <p className="text-slate-600">
                Digian.ai will, taking into account the nature of the processing, assist the Controller by appropriate technical and organisational measures to fulfil the Controller's obligation to respond to requests from Data Subjects exercising their rights (access, rectification, erasure, portability, objection, restriction of processing) under applicable Data Protection Laws.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Term and Termination</h2>
              <p className="text-slate-600">
                This DPA remains in force for the duration of the services agreement and terminates automatically upon its expiry or termination. Upon termination, Digian.ai will, at the Controller's election, securely delete or return all Personal Data within 30 days, unless applicable law requires longer retention.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Contact Us</h2>
              <p className="text-slate-600 mb-4">
                For DPA-related inquiries, to request a signed DPA, or to obtain the current sub-processor list, please contact:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                <p className="text-slate-600"><strong>Email:</strong> <a href="mailto:contact@digian.ai" className="text-indigo-600 hover:text-indigo-800">contact@digian.ai</a></p>
                <p className="text-slate-600"><strong>Website:</strong> <a href="https://digian.ai" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">digian.ai</a></p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DPAPage;
